import React, { useEffect, useRef, useId } from 'react';
import { X } from 'lucide-react';
// Paleta institucional Elecom — fuente única de verdad para los tokens de color
// (ver src/theme/colors.js).
import { COLORS as C } from '../theme/colors';

// Selector de elementos enfocables — se usa tanto para el foco inicial como
// para atrapar el foco (Tab/Shift+Tab) dentro del modal mientras está abierto.
const FOCUSABLE_SELECTOR = [
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * @description Componente de modal reutilizable para mostrar contenido en una ventana emergente.
 * Chrome institucional: overlay oscuro translúcido, panel con header fijo + cuerpo
 * como única área de scroll (el pie de cada formulario queda "sticky" al fondo del
 * cuerpo, ver .modal-footer en src/styles/responsive.css) y bottom sheet a pantalla
 * completa en mobile (≤640px, mismo archivo).
 *
 * Comportamiento (rediseño de modales — coherencia y accesibilidad):
 * - Cierra con Escape o con clic en el overlay; un clic dentro del panel nunca cierra.
 * - Enfoca el primer campo al abrir, atrapa el foco (Tab) dentro del panel mientras
 *   está abierto, y devuelve el foco al elemento que lo abrió al cerrar.
 * - role="dialog" + aria-modal="true" + aria-labelledby apuntando al título.
 * - Bloquea el scroll del body mientras está abierto.
 *
 * @param {object} props Las propiedades del componente.
 * @param {boolean} props.show Booleano que determina si el modal se muestra o no.
 * @param {Function} props.onClose Función que se llama cuando se intenta cerrar el modal.
 * @param {string} props.title El título que se mostrará en la cabecera del modal.
 * @param {React.ReactNode} props.children El contenido a renderizar dentro del cuerpo del modal.
 * @param {object} [props.modalStyle] Estilos adicionales para sobreescribir el contenedor del modal (e.g., ancho).
 * @returns {JSX.Element | null} El modal renderizado o `null` si `show` es `false`.
 */
const Modal = ({ show, onClose, title, children, modalStyle }) => {
  const titleId = useId();
  const panelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Bloquea el scroll del body, recuerda quién abrió el modal para devolverle
  // el foco al cerrar, y enfoca el primer campo del formulario al abrir.
  useEffect(() => {
    if (!show) return undefined;

    previouslyFocusedRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Se difiere un tick para que el panel ya esté montado en el DOM.
    const focusTimeout = setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const firstField = panel.querySelector(
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])'
      );
      const target = firstField || panel.querySelector(FOCUSABLE_SELECTOR);
      if (target) target.focus();
    }, 0);

    return () => {
      clearTimeout(focusTimeout);
      document.body.style.overflow = previousOverflow;
      const previouslyFocused = previouslyFocusedRef.current;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [show]);

  // Cierra con Escape mientras el modal está abierto.
  useEffect(() => {
    if (!show) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [show, onClose]);

  if (!show) {
    return null;
  }

  // Atrapa el foco dentro del panel: Tab en el último elemento vuelve al
  // primero, Shift+Tab en el primero salta al último.
  const handleTrapFocus = (e) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;

    const focusable = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Cierra solo si el clic fue directamente sobre el overlay (nunca si burbujea
  // desde un clic dentro del panel).
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={styles.overlay} className="modal-overlay" onClick={handleOverlayClick}>
      <div
        ref={panelRef}
        style={{ ...styles.modal, ...modalStyle }}
        className="modal-inner"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleTrapFocus}
      >
        <div style={styles.header}>
          <h2 id={titleId} style={styles.title}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            aria-label="Cerrar"
          >
            <X size={18} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
        <div style={styles.body} className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

// Estilos del modal — alineados a la paleta charcoal institucional.
// El chrome estructural (radio, sombra, overlay, header fijo, cuerpo como
// única área de scroll) vive acá; el bottom sheet mobile y los estados
// :hover/:focus de sus controles viven en src/styles/responsive.css.
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    overflowY: 'auto',
    padding: '20px 0',
    zIndex: 1000,
  },
  modal: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: C.white,
    borderRadius: '10px',
    width: '560px',
    maxWidth: '90%',
    maxHeight: '90vh',
    boxShadow: '0 4px 24px rgba(56, 62, 65, 0.1)',
    border: `1px solid rgba(56, 62, 65, 0.08)`,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    padding: '18px 24px',
    borderBottom: `1px solid rgba(56, 62, 65, 0.08)`,
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: C.charcoal,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
};

export default Modal;
